import { useEffect, useState, useCallback } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  MapPin,
  Calendar,
  Mountain,
  X,
  ChevronLeft,
  ChevronRight,
  Quote,
  Film,
} from "lucide-react";
import { treks } from "../data/content";
import CommentsSection from "../components/CommentsSection";

// ---------------------------------------------------------------------------
// Lightbox — inline component for simplicity
// ---------------------------------------------------------------------------
const Lightbox = ({ images, index, onClose, onPrev, onNext }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, onPrev, onNext]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[100] bg-[#15140F]/95 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
      data-testid="trek-lightbox"
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-5 right-5 text-[#FAF9F6] hover:text-[#CC7A52] transition-colors"
        aria-label="Close"
        data-testid="lightbox-close"
      >
        <X size={28} />
      </button>
      <div className="absolute top-5 left-5 text-[#FAF9F6]/70 text-xs tracking-widest uppercase">
        {index + 1} / {images.length}
      </div>

      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onPrev();
            }}
            className="absolute left-4 sm:left-8 text-[#FAF9F6] hover:text-[#CC7A52] transition-colors p-2"
            aria-label="Previous"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={36} strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            className="absolute right-4 sm:right-8 text-[#FAF9F6] hover:text-[#CC7A52] transition-colors p-2"
            aria-label="Next"
            data-testid="lightbox-next"
          >
            <ChevronRight size={36} strokeWidth={1.5} />
          </button>
        </>
      )}

      <motion.img
        key={images[index]}
        src={images[index]}
        alt={`Photograph ${index + 1}`}
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="max-h-[88vh] max-w-[92vw] object-contain shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
// Trek detail page
// ---------------------------------------------------------------------------
export default function TrekDetail() {
  const { slug } = useParams();
  const trek = treks.find((t) => t.slug === slug);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const prevImage = useCallback(() => {
    setLightboxIndex((i) =>
      i === null ? null : (i - 1 + trek.gallery.length) % trek.gallery.length
    );
  }, [trek]);
  const nextImage = useCallback(() => {
    setLightboxIndex((i) =>
      i === null ? null : (i + 1) % trek.gallery.length
    );
  }, [trek]);

  if (!trek) return <Navigate to="/trek-and-travel" replace />;

  return (
    <div data-testid="page-trek-detail">
      {/* Hero */}
      <section className="relative">
        <div className="relative h-[55vh] sm:h-[65vh] min-h-[400px] overflow-hidden">
          <img
            src={trek.coverImage}
            alt={`${trek.name} hero`}
            className="w-full h-full object-cover"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to bottom, rgba(44,42,40,0.15) 0%, rgba(44,42,40,0.55) 70%, rgba(44,42,40,0.85) 100%)",
            }}
          />
          <div className="absolute inset-x-0 bottom-0">
            <div className="container-narrow pb-10 sm:pb-16">
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-[#EAE5DC] eyebrow !text-[#CC7A52]"
              >
                Trek & Travel
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.08 }}
                className="font-serif text-[#FAF9F6] mt-3 text-4xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-4xl"
              >
                {trek.name}
              </motion.h1>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.16 }}
                className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[#EAE5DC] text-sm"
              >
                <span className="inline-flex items-center gap-1.5">
                  <MapPin size={14} strokeWidth={1.7} />
                  {trek.region}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Calendar size={14} strokeWidth={1.7} />
                  {trek.month}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Mountain size={14} strokeWidth={1.7} />
                  {trek.elevation}
                </span>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Back link */}
      <section className="pt-8">
        <div className="container-narrow">
          <Link
            to="/trek-and-travel"
            data-testid="trek-detail-back"
            className="inline-flex items-center gap-1.5 text-sm text-[#5C5852] hover:text-[#4A6B53] transition-colors"
          >
            <ArrowLeft size={15} strokeWidth={1.8} />
            All treks
          </Link>
        </div>
      </section>

      {/* About + Memory */}
      <section className="py-12 sm:py-16">
        <div className="container-narrow grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7">
            <p className="eyebrow">About this trek</p>
            <div className="mt-5 space-y-5 text-[#2C2A28] leading-relaxed text-lg">
              {trek.about.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
          {trek.memory && (
            <aside className="lg:col-span-5">
              <div className="rounded-sm bg-[#F2EFEB] border border-[#D8D3CC] p-7 sm:p-8 sticky top-28">
                <div className="flex items-center gap-2">
                  <Quote size={16} className="text-[#CC7A52]" strokeWidth={1.7} />
                  <p className="eyebrow !text-[#CC7A52] !text-[10px]">
                    A moment I won&apos;t forget
                  </p>
                </div>
                <p className="mt-4 text-[#2C2A28] leading-relaxed italic">
                  {trek.memory}
                </p>
              </div>
            </aside>
          )}
        </div>
      </section>

      {/* Gallery — masonry via CSS columns */}
      {trek.gallery?.length > 0 && (
        <section className="pb-16 sm:pb-20">
          <div className="container-narrow">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
              <div>
                <p className="eyebrow">Photographs</p>
                <h2 className="font-serif text-4xl sm:text-5xl mt-3 text-[#2C2A28]">
                  From the trail.
                </h2>
              </div>
              <p className="text-[#5C5852] text-sm max-w-sm">
                Click any photograph to view it in full. Arrow keys move between
                photos.
              </p>
            </div>

            <div
              className="columns-1 sm:columns-2 lg:columns-3 gap-4 sm:gap-5 [column-fill:_balance]"
              data-testid="trek-gallery"
            >
              {trek.gallery.map((src, i) => (
                <motion.button
                  type="button"
                  key={`${src}-${i}`}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
                  onClick={() => setLightboxIndex(i)}
                  className="mb-4 sm:mb-5 w-full block break-inside-avoid rounded-sm overflow-hidden border border-[#D8D3CC] bg-[#F2EFEB] group focus:outline-none focus:ring-2 focus:ring-[#4A6B53]/60"
                  data-testid={`trek-gallery-item-${i}`}
                >
                  <img
                    src={src}
                    alt={`${trek.name} photograph ${i + 1}`}
                    loading="lazy"
                    className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                  />
                </motion.button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Videos */}
      {trek.videos?.length > 0 && (
        <section className="pb-20 sm:pb-28">
          <div className="container-narrow">
            <div className="mb-8">
              <div className="flex items-center gap-2">
                <Film size={16} className="text-[#CC7A52]" strokeWidth={1.7} />
                <p className="eyebrow !text-[#CC7A52] !text-[10px]">From the trail</p>
              </div>
              <h2 className="font-serif text-4xl sm:text-5xl mt-3 text-[#2C2A28]">
                Videos.
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6" data-testid="trek-videos">
              {trek.videos.map((id, i) => (
                <div
                  key={`${id}-${i}`}
                  className="rounded-sm overflow-hidden border border-[#D8D3CC] bg-[#2C2A28]"
                  data-testid={`trek-video-${i}`}
                >
                  <div className="aspect-video">
                    <iframe
                      title={`${trek.name} video ${i + 1}`}
                      src={`https://www.youtube.com/embed/${id}`}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer nav */}
      <section className="pb-20">
        <div className="container-narrow border-t border-[#D8D3CC] pt-10">
          <CommentsSection
            page="Trek & Travel"
            itemId={trek.slug}
            itemTitle={trek.name}
          />
          <div className="mt-10">
            <Link
              to="/trek-and-travel"
              data-testid="trek-detail-back-bottom"
              className="inline-flex items-center gap-1.5 text-[#4A6B53] hover:text-[#3A5441] transition-colors"
            >
              <ArrowLeft size={16} strokeWidth={1.8} />
              Back to all treks
            </Link>
          </div>
        </div>
      </section>

      <AnimatePresence>
        {lightboxIndex !== null && trek.gallery?.length > 0 && (
          <Lightbox
            images={trek.gallery}
            index={lightboxIndex}
            onClose={closeLightbox}
            onPrev={prevImage}
            onNext={nextImage}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
